import { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';

function parseTurkishNumber(val) {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'number') return val;
  const s = String(val).trim();
  const turkish = parseFloat(s.replace(/\./g, '').replace(',', '.'));
  if (!isNaN(turkish)) return turkish;
  const plain = parseFloat(s.replace(/,/g, ''));
  if (!isNaN(plain)) return plain;
  return null;
}

function pad2(n) { return String(n).padStart(2, '0'); }

function parseDateTime(val) {
  if (!val && val !== 0) return { tarih: '', saat: '' };
  if (typeof val === 'number') {
    const date = XLSX.SSF.parse_date_code(val);
    if (!date) return { tarih: '', saat: '' };
    return {
      tarih: `${pad2(date.d)}.${pad2(date.m)}.${date.y}`,
      saat: `${pad2(date.H || 0)}:${pad2(date.M || 0)}`
    };
  }
  const str = String(val);
  const dashIdx = str.indexOf('-', 6);
  const datePart = dashIdx > 0 ? str.slice(0, dashIdx) : str;
  const timePart = dashIdx > 0 ? str.slice(dashIdx + 1) : '';
  const [d, m, y] = datePart.split('/');
  if (!d || !m || !y) return { tarih: '', saat: '' };
  const timeBits = (timePart || '00:00').split(':');
  return {
    tarih: `${pad2(d)}.${pad2(m)}.${y}`,
    saat: `${pad2(timeBits[0] || 0)}:${pad2(timeBits[1] || 0)}`
  };
}

export default function ExcelImportModal({
  onClose, gelirEkle, giderEkle,
  gelirGruplari, giderGruplari, odemeTipleri, bankalar
}) {
  const [satirlar, setSatirlar] = useState(null);
  const [secili, setSecili] = useState({});
  const [gelirGrubuId, setGelirGrubuId] = useState(gelirGruplari[0]?.gelirGrubuId || '');
  const [giderGrubuId, setGiderGrubuId] = useState(giderGruplari[0]?.giderGrubuId || '');
  const [banka, setBanka] = useState(bankalar[0]?.ad || bankalar[0] || '');
  const [odemeTip, setOdemeTip] = useState('BANKA HAVALE');
  const [yukleniyor, setYukleniyor] = useState(false);
  const [sonuc, setSonuc] = useState(null);
  const fileRef = useRef();

  // Arka sayfa scroll kilidi
  useEffect(function () {
    document.body.style.overflow = 'hidden';
    return function () { document.body.style.overflow = 'unset'; };
  }, []);

  function dosyaSec(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = function (ev) {
      try {
        const data = new Uint8Array(ev.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const sh = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sh, { header: 1, raw: true, defval: '' });
        let headerIdx = -1;
        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          const first = String(row[0] || '').trim();
          if (first === 'Tarih/Saat' || first.startsWith('Tarih/Saat')) { headerIdx = i; break; }
        }
        if (headerIdx === -1) {
          for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (row && String(row[0] || '').includes('Tarih/Saat')) { headerIdx = i; break; }
          }
        }
        if (headerIdx === -1) { alert('Excel formatı tanınamadı. "Tarih/Saat" sütunu bulunamadı.'); return; }
        const header = rows[headerIdx].map(c => String(c).trim());
        const colTarih = 0;
        const colTutar = header.findIndex(c => c.includes('Tutar'));
        const colAciklama = header.length - 1;
        const aciklamaByName = header.findIndex(c => c.toLowerCase().includes('klama'));
        const colAciklamaFinal = aciklamaByName >= 0 ? aciklamaByName : colAciklama;
        const parsed = [];
        for (let i = headerIdx + 1; i < rows.length; i++) {
          const row = rows[i];
          if (!row || !row[colTarih] || String(row[colTarih]).trim() === '') continue;
          const tutarRaw = colTutar >= 0 ? row[colTutar] : '';
          const tutar = parseTurkishNumber(tutarRaw);
          if (tutar === null || tutar === 0) continue;
          const { tarih, saat } = parseDateTime(row[colTarih]);
          if (!tarih) continue;
          const aciklama = String(row[colAciklamaFinal] || '').trim();
          parsed.push({ idx: i, tarih, saat, aciklama, tutar, tip: tutar >= 0 ? 'gelir' : 'gider' });
        }
        setSatirlar(parsed);
        const sel = {};
        parsed.forEach(r => { sel[r.idx] = true; });
        setSecili(sel);
        setSonuc(null);
      } catch (err) {
        alert('Dosya okunamadı: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  function hepsiniSec(val) {
    const sel = {};
    (satirlar || []).forEach(r => { sel[r.idx] = val; });
    setSecili(sel);
  }

  async function iceAktar() {
    const seciliSatirlar = (satirlar || []).filter(r => secili[r.idx]);
    if (seciliSatirlar.length === 0) { alert('İçe aktarılacak satır seçiniz.'); return; }
    setYukleniyor(true);
    let basarili = 0, hatali = 0;
    for (const satir of seciliSatirlar) {
      try {
        if (satir.tip === 'gelir') {
          await gelirEkle({
            ad: satir.aciklama, tarih: satir.tarih, saat: satir.saat,
            tutar: satir.tutar, paraBirimi: 'TL', kurDegeri: 1,
            odemeTip, banka, gelirGrubuId: parseInt(gelirGrubuId) || 0, personelId: null,
          });
        } else {
          await giderEkle({
            ad: satir.aciklama, tarih: satir.tarih, saat: satir.saat,
            tutar: Math.abs(satir.tutar), miktar: 1, paraBirimi: 'TL', kurDegeri: 1,
            odemeTip, banka, giderGrubuId: parseInt(giderGrubuId) || null,
            altTip: 'gider', personelHedefId: null, personelId: null,
          });
        }
        basarili++;
      } catch { hatali++; }
    }
    setYukleniyor(false);
    setSonuc({ basarili, hatali });
  }

  const gelirSayisi = (satirlar || []).filter(r => r.tip === 'gelir' && secili[r.idx]).length;
  const giderSayisi = (satirlar || []).filter(r => r.tip === 'gider' && secili[r.idx]).length;

  const SEL = 'w-full bg-slate-50 border-2 border-transparent focus:border-indigo-400 rounded-xl px-4 py-3 text-xs font-black text-slate-700 outline-none uppercase tracking-widest transition-all appearance-none cursor-pointer';
  const LBL = 'block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 pl-1';

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9000] flex items-center justify-center p-4"
      onClick={function (e) { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-fade-in">

        {/* Başlık */}
        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-8 py-6 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center text-2xl shadow-inner">📥</div>
            <div>
              <h2 className="text-xl font-black text-white tracking-tight leading-none">Excel'den İçe Aktar</h2>
              <p className="text-white/60 text-[10px] font-black uppercase tracking-widest mt-1.5">Banka Hesap Özeti · .xls / .xlsx</p>
            </div>
          </div>
          <button onClick={onClose} className="w-10 h-10 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center justify-center font-black text-lg transition-all">✕</button>
        </div>

        <div className="flex flex-col gap-6 overflow-y-auto p-8 flex-1 custom-scrollbar">

          {/* Dosya Seç */}
          <div className="bg-slate-50 rounded-2xl p-6 border-2 border-dashed border-slate-200 hover:border-emerald-300 transition-colors">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                onClick={function () { fileRef.current.click(); }}
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-black text-[10px] uppercase tracking-widest px-6 py-3 rounded-xl shadow-lg shadow-emerald-200 transition-all active:scale-95 flex items-center gap-2 flex-shrink-0"
              >
                <span>📂</span> Dosya Seç
              </button>
              <div className="flex flex-col gap-1">
                <p className="text-sm font-black text-slate-700">
                  {satirlar ? `✅ ${satirlar.length} satır okundu` : 'Henüz dosya seçilmedi'}
                </p>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  Desteklenen format: .xls · .xlsx — Tarih/Saat, Tutar, Açıklama sütunları gerekli
                </p>
              </div>
            </div>
            <input ref={fileRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={dosyaSec} />
          </div>

          {/* Yapılandırma */}
          {satirlar && (
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <p className={LBL}>Aktarım Ayarları</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className={LBL}>Gelir Grubu</label>
                  <select value={gelirGrubuId} onChange={function (e) { setGelirGrubuId(e.target.value); }} className={SEL}>
                    {gelirGruplari.map(g => <option key={g.gelirGrubuId} value={g.gelirGrubuId}>{g.ad}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LBL}>Gider Grubu</label>
                  <select value={giderGrubuId} onChange={function (e) { setGiderGrubuId(e.target.value); }} className={SEL}>
                    {giderGruplari.map(g => <option key={g.giderGrubuId} value={g.giderGrubuId}>{g.ad}</option>)}
                  </select>
                </div>
                <div>
                  <label className={LBL}>Banka</label>
                  <select value={banka} onChange={function (e) { setBanka(e.target.value); }} className={SEL}>
                    <option value="">— Seçiniz —</option>
                    {bankalar.map(function (b, i) {
                      const ad = b.ad || b;
                      return <option key={i} value={ad}>{ad}</option>;
                    })}
                  </select>
                </div>
                <div>
                  <label className={LBL}>Ödeme Tipi</label>
                  <select value={odemeTip} onChange={function (e) { setOdemeTip(e.target.value); }} className={SEL}>
                    {(odemeTipleri.length > 0 ? odemeTipleri : ['BANKA HAVALE', 'NAKİT', 'KREDİ KARTI']).map(function (o, i) {
                      const ad = o.ad || o;
                      return <option key={i} value={ad}>{ad}</option>;
                    })}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Önizleme */}
          {satirlar && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl">{gelirSayisi} Gelir</span>
                  <span className="bg-rose-100 text-rose-700 text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-xl">{giderSayisi} Gider</span>
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">seçili</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={function () { hepsiniSec(true); }} className="text-[10px] font-black text-emerald-600 hover:text-emerald-700 uppercase tracking-widest transition-colors">Tümünü Seç</button>
                  <span className="text-slate-200">|</span>
                  <button onClick={function () { hepsiniSec(false); }} className="text-[10px] font-black text-slate-400 hover:text-slate-600 uppercase tracking-widest transition-colors">Temizle</button>
                </div>
              </div>
              <div className="border-2 border-slate-100 rounded-2xl overflow-hidden max-h-64 overflow-y-auto custom-scrollbar">
                <table className="w-full">
                  <thead className="bg-slate-50/80 border-b border-slate-100 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 w-10"></th>
                      <th className="px-4 py-3 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Tarih/Saat</th>
                      <th className="px-4 py-3 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Açıklama</th>
                      <th className="px-4 py-3 text-right text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">Tutar (TL)</th>
                      <th className="px-4 py-3 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">Tip</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {satirlar.map(function (s) {
                      return (
                        <tr key={s.idx} className={'transition-colors ' + (secili[s.idx] ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 opacity-40')}>
                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={!!secili[s.idx]}
                              onChange={function (e) { setSecili(prev => ({ ...prev, [s.idx]: e.target.checked })); }}
                              className="w-4 h-4 accent-emerald-500 cursor-pointer"
                            />
                          </td>
                          <td className="px-4 py-3 text-[11px] font-bold text-slate-500 whitespace-nowrap">{s.tarih} {s.saat}</td>
                          <td className="px-4 py-3 text-xs font-bold text-slate-700 max-w-xs truncate" title={s.aciklama}>{s.aciklama}</td>
                          <td className={'px-4 py-3 text-right text-xs font-black whitespace-nowrap ' + (s.tip === 'gelir' ? 'text-emerald-600' : 'text-rose-600')}>
                            {s.tutar.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {s.tip === 'gelir'
                              ? <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest">Gelir</span>
                              : <span className="bg-rose-100 text-rose-700 px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest">Gider</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sonuç */}
          {sonuc && (
            <div className={'rounded-2xl px-6 py-4 font-black text-sm ' + (sonuc.hatali === 0 ? 'bg-emerald-50 text-emerald-700 border-2 border-emerald-100' : 'bg-amber-50 text-amber-700 border-2 border-amber-100')}>
              {sonuc.hatali === 0
                ? `✅ ${sonuc.basarili} kayıt başarıyla aktarıldı.`
                : `✅ ${sonuc.basarili} kayıt aktarıldı · ⚠️ ${sonuc.hatali} kayıt hatalı.`}
            </div>
          )}
        </div>

        {/* Alt Butonlar */}
        <div className="px-8 py-5 border-t border-slate-100 flex items-center justify-between gap-3 flex-shrink-0 bg-slate-50/50 rounded-b-[2.5rem]">
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest hidden sm:block">
            {satirlar ? `${satirlar.length} satır · ${gelirSayisi + giderSayisi} seçili` : 'Dosya bekleniyor'}
          </p>
          <div className="flex items-center gap-3 ml-auto">
            <button
              onClick={onClose}
              className="h-12 px-8 border-2 border-slate-200 text-slate-600 hover:bg-slate-100 font-black text-[10px] uppercase tracking-widest rounded-2xl transition-all active:scale-95"
            >
              {sonuc ? 'Kapat' : 'İptal'}
            </button>
            {satirlar && !sonuc && (
              <button
                onClick={iceAktar}
                disabled={yukleniyor || gelirSayisi + giderSayisi === 0}
                className="h-12 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 disabled:from-slate-300 disabled:to-slate-300 text-white font-black text-[10px] uppercase tracking-widest rounded-2xl shadow-lg shadow-emerald-200 transition-all active:scale-95 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {yukleniyor
                  ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span> Aktarılıyor...</>
                  : <><span>📤</span> {gelirSayisi + giderSayisi} Kayıt Aktar</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
